import { z } from 'zod';
import { partition } from 'lodash';
import type { CmsNumber, CmsRichText, CmsShortText } from '$cms/types';
import { CmsNumberSchema, CmsRichTextSchema, CmsShortTextSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ComparisonTableRow, ComparisonTableService } from '@/components/molecules/ComparisonTable';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';

export type CompareServicesTranslatedEntities = SchemaType<
  (typeof CompareServicesSchema)['translatedEntities']
>;

export interface LinkToPageProps {
  readonly linkToPageContent: string;
  readonly linkToPage: string;
  readonly linkToPageCTAText: string;
}

export const ColValuesSchema = z.record(z.string(), z.union([CmsShortTextSchema, CmsNumberSchema]));
export const RowColValuesSchema = z.record(z.string(), ColValuesSchema);

export type RowColValues = z.infer<typeof RowColValuesSchema>;
export type ColValues = z.infer<typeof ColValuesSchema>;

export interface ServiceData extends ComparisonTableService {
  readonly info: React.ReactNode;
  readonly description: React.ReactNode;
}

export const buildServiceArray = (
  productNames: Record<string, CmsShortText>,
  productPrices: Record<string, CmsNumber>,
  productInfo: Record<string, CmsRichText>,
  productDescriptions: Record<string, CmsRichText>,
  popularProductIndex?: CmsNumber | null
): ServiceData[] => {
  const productsArray: string[] = Object.values(productNames).map(({ value }) => value);
  const pricesArray: number[] = Object.values(productPrices).map(({ number }) => number);
  const infoArray: React.ReactNode[] = Object.values(productInfo).map(({ content }) =>
    renderRichTextContent(content)
  );
  const descriptionArray: React.ReactNode[] = Object.values(productDescriptions).map(({ content }) =>
    renderRichTextContent(content)
  );
  const servicesData: ServiceData[] = productsArray.map((currentService, index) => ({
    name: currentService,
    price: pricesArray[index],
    popular: !!popularProductIndex && index === popularProductIndex.number - 1,
    info: infoArray[index],
    description: descriptionArray[index],
  }));
  return servicesData;
};

type ColArray = (string | boolean)[];

const buildServicesValuesArray = (rowCol: ColValues, defaultArray: ColArray) => {
  return Object.entries(rowCol).reduce(
    (acc, [index, rowColValue]) => {
      const val = 'value' in rowColValue ? rowColValue.value : !!rowColValue.number;
      acc[Number(index) - 1] = val;
      return acc;
    },
    [...defaultArray]
  );
};

export const buildLineItemsArray = (
  lineItemTitles: Record<string, CmsShortText>,
  rowColValues: RowColValues,
  noProducts: number
): ComparisonTableRow[] => {
  const defaultColValues = Array(noProducts).fill(false);

  const rowColArray: ColArray[] = [];
  Object.entries(rowColValues).forEach(([key, value]) => {
    const index = Number(key.replace(/\D/g, '')) - 1;
    const servicesValuesArray = buildServicesValuesArray(value, defaultColValues);
    rowColArray[index] = servicesValuesArray;
  });

  const rows: ComparisonTableRow[] = Object.values(lineItemTitles).map((lineItem, index) => ({
    lineItemName: lineItem.value,
    servicesValues: rowColArray[index] ?? defaultColValues,
  }));

  return rows;
};

const fixedTranslatedEntities = z.object({
  ourOfferText: CmsShortTextSchema,
  mostPopularText: CmsShortTextSchema,
  popularText: CmsShortTextSchema,
  startingFormText: CmsShortTextSchema,
  getQuoteText: CmsShortTextSchema,
  compareOffersText: CmsShortTextSchema,
  linkToPage: CmsShortTextSchema.optional(),
  linkToPageContent: CmsShortTextSchema.optional(),
  linkToPageCTAText: CmsShortTextSchema.optional(),
});

const tableTranslatedEntities = z.object({
  productNames: z.record(CmsShortTextSchema),
  productPrices: z.record(CmsNumberSchema),
  productInfo: z.record(CmsRichTextSchema),
  productDescriptions: z.record(CmsRichTextSchema),
  popularProductIndex: CmsNumberSchema.optional(),
  lineItemTitles: z.record(CmsShortTextSchema),
});

/**
 * This component will parse initially using passthrough
 * because the table has a variable number of columns and rows and
 * we don't initially know the shape of the keys describing their values
 * These values will be validated later when the data is processed
 */
export const CompareServicesSchema = {
  translatedEntities: z
    .object({})
    .passthrough()
    .transform((data) => {
      /**
       * Splitting between keys that will build the table columns and will be in the form:
       * - row1col1 row1col2 ... row1colN
       * - ...
       * - rowMcol1 rowMcol2 ... rowMcolN
       *
       * and the fixed translation keys.
       */
      const [rowColObj, knownTranslations] = partition(Object.entries(data), ([key]) =>
        /^row[1-9]+col$/g.test(key)
      ).map((entries) => Object.fromEntries(entries));

      const rowColValues = RowColValuesSchema.parse(rowColObj);
      const fixedTranslations = fixedTranslatedEntities.parse(knownTranslations);
      const {
        productNames,
        productPrices,
        productInfo,
        productDescriptions,
        popularProductIndex,
        lineItemTitles,
      } = tableTranslatedEntities.parse(knownTranslations);

      const services: ServiceData[] = buildServiceArray(
        productNames,
        productPrices,
        productInfo,
        productDescriptions,
        popularProductIndex
      );

      const lineItems: ComparisonTableRow[] = buildLineItemsArray(
        lineItemTitles,
        rowColValues,
        services.length
      );

      return {
        ...fixedTranslations,
        services,
        lineItems,
      };
    }),
};
